import {
  motion, MotionConfig,
} from 'framer-motion';
import { useCallback, useEffect, useState } from 'react';
import { DatePicker } from 'antd';
import moment from 'moment';

import { useI18n } from 'context/i18n';
import { saveFirstRace, useKboixUser } from 'actions/nnormal-world';
import { useLink } from 'hooks';
import { Timer } from '../timer';

import styles from './style.module.css';

const defaultSteps = [
  'gender',
  'goals',
  'race_type',
  'race_name',
  'race_date',
  'altitude',
  'train_respiratory',
  'heat',
  'train_high_carb',
  'tread',
  'mode',
  'foot_width',
  'saving',
];

export function KboixTipsForm() {
  const { t } = useI18n();
  const to = useLink();
  const { data: user } = useKboixUser(true);

  const [ state, setState ] = useState('idle'); // idle, saving, success, error
  const [ steps, setSteps ] = useState(defaultSteps);
  const [ step, setStep ] = useState(0);
  const [ lastStep, setLastStep ] = useState(0);

  const [ values, setValues ] = useState({
    gender: undefined,
    goals: undefined,
    race_type: undefined,
    race_name: undefined,
    race_date: undefined,
    altitude: undefined,
    train_respiratory: undefined,
    heat: undefined,
    train_high_carb: undefined,
    tread: undefined,
    mode: undefined,
    foot_width: undefined,
  });

  useEffect(() => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    if (params.get('test') !== 'true' && user?.isCompletedForm === true) {
      window.location.assign(to('/myaccount/nnormal-world/tips'));
    }
  }, [ user, user?.isCompletedForm ]);

  const nextStep = () => {
    setTimeout(() => {
      if (lastStep === step) {
        setLastStep(step + 1);
      }

      setStep((prev) => prev + 1);
    }, 200);
  };

  const updateData = (key, value, go = true) => {
    // if user selects fit goal value, remove 3
    if (key === 'goals' && value === 2) {
      const stepsWithoutRace = [ ...defaultSteps ].filter((s) => ![ 'race_type',
        'race_name',
        'race_date' ].includes(s));
      setSteps(stepsWithoutRace);
      setLastStep(step);
    } else if (key === 'goals' && value === 1 && steps.length !== defaultSteps.length) {
      setSteps(defaultSteps);
      setLastStep(step);
    }
    setValues((prev) => ({ ...prev, [key]: value }));
    if (go) {
      nextStep();
    }
  };

  const saveData = useCallback(async () => {
    try {
      setState('saving');

      const body = {
        race: {
          name: values.race_name,
          type: values.race_type,
          date: values.race_date,
        },
        gender: values.gender,
        goals: values.goals,
        tread: values.tread,
        mode: values.mode,
        foot_width: values.foot_width,
        altitude: values.altitude === 'yes',
        heat: values.heat === 'yes',
        train_high_carb: values.train_high_carb === 'yes',
        train_respiratory: values.train_respiratory === 'yes',
      };
      // if your goal is to get fit we don't create a race in this first form. So we set race to null.
      if (body.goals === 2) {
        body.race = null;
      }
      await saveFirstRace(body);
      setState('success');
      setTimeout(() => {
        if (typeof window !== 'undefined') {
          window.location.assign(to('/myaccount/nnormal-world/tips'));
        }
      });
    } catch (error) {
      console.error(error);
      setState('error');
    }
  }, [ values, state ]);

  useEffect(() => {
    if (step === steps.length - 1) {
      saveData();
    }
  }, [ step ]);

  return (
    <MotionConfig transition={{ ease: [ 0.32, 0.72, 0, 1 ], duration: 0.3 }}>
      <div className={styles.container}>
        <div className={styles.navButtons} >
          <button
            disabled={step === 0 || state === 'saving'}
            onClick={() => {
              setStep((prev) => (prev > 0 ? prev - 1 : prev));
            }}
            className={styles.back}
          >
            {t('kboix', 'back')}
          </button>
          <div className={styles.buttonSeparator}></div>
          <button
            disabled={step >= lastStep || state === 'saving' || (steps.indexOf('race_name') === step && values.race_name === '')}
            data-hidden={step === steps.length - 1}
            onClick={nextStep}
            className={styles.nextTop}
          >
            {t('kboix', 'next')}
          </button>
        </div>
        <div className={styles.form} >
          <motion.div animate={{ x: `${step * -100}%` }} className={styles.list}>
            <div className={styles.item}>
              <p className={styles.question} >{t('kboix.register', 'question.gender')}</p>
              <div className={styles.gender}>
                <button className={styles.genderOption} data-selected={values.gender === 2} onClick={() => updateData('gender', 2)}><span>M</span>{t('kboix', 'gender.2')}</button>
                <button className={styles.genderOption} data-selected={values.gender === 1} onClick={() => updateData('gender', 1)}><span>W</span>{t('kboix', 'gender.1')}</button>
              </div>
            </div>
            <div className={styles.item}>
              <p className={styles.question} >{t('kboix.register', 'question.goals')}</p>
              <div className={styles.answers}>
                <button className={styles.option} data-selected={values.goals === 1} onClick={() => updateData('goals', 1)}>{t('kboix', 'goals.1')}</button>
                <button className={styles.option} data-selected={values.goals === 2} onClick={() => updateData('goals', 2)}>{t('kboix', 'goals.2')}</button>
              </div>
            </div>
            {steps.includes('race_type') ? (
              <div className={styles.item}>
                <p className={styles.question}>{t('kboix.register', 'question.race.type')}</p>
                <div className={styles.answers}>
                  <button className={styles.option} data-selected={values.race_type === 1} onClick={() => updateData('race_type', 1)}>{t('kboix', 'race.type.1')}</button>
                  <button className={styles.option} data-selected={values.race_type === 2} onClick={() => updateData('race_type', 2)}>{t('kboix', 'race.type.2')}</button>
                  <button className={styles.option} data-selected={values.race_type === 3} onClick={() => updateData('race_type', 3)}>{t('kboix', 'race.type.3')}</button>
                </div>
              </div>
            ) : null}
            {steps.includes('race_name') ? (
            <div className={styles.item}>
              <p className={styles.question}>{t('kboix.register', 'question.race.name')}</p>
              <div className={styles.answers}>
                <textarea value={values.race_name} className={styles.textarea} onChange={(e) => updateData('race_name', e.currentTarget.value, false)} placeholder={t('kboix.register', 'race.name.placeholder')} rows={4}></textarea>
                <p className={styles.questionDesc}>{t('kboix.register', 'required')}</p>
                <button disabled={!values.race_name || values.race_name.trim() === ''} className={styles.nextButton} onClick={nextStep}>{t('kboix', 'next')}</button>
              </div>
            </div>
            ) : null}
            {steps.includes('race_date') ? (
              <div className={styles.item}>
                <p className={styles.question}>{t('kboix.register', 'question.race.date')}</p>
                <div className={styles.answers}>
                  <DatePicker
                    className={styles.datePicker}
                    allowClear={false}
                    showToday={false}
                    disabledDate={(current) => {
                      if (current && current < moment().startOf('day')) {
                        return true;
                      }
                      return false;
                    }}
                    onChange={(value) => updateData('race_date', value.format('YYYY-MM-DD'), false)}
                  />
                  <p className={styles.questionDesc}>{t('kboix.register', 'date.desc')}</p>
                  <button disabled={!values.race_date} className={styles.nextButton} onClick={nextStep}>{t('kboix', 'next')}</button>
                </div>
              </div>
            ) : null}
            <div className={styles.item}>
              <p className={styles.question}>{t('kboix.register', 'question.altitude')}</p>
              <div className={styles.answers}>
                <button className={styles.option} data-selected={values.altitude === 'yes'} onClick={() => updateData('altitude', 'yes')}>{t('kboix.register', 'yes')}</button>
                <button className={styles.option} data-selected={values.altitude === 'no'} onClick={() => updateData('altitude', 'no')}>{t('kboix.register', 'no')}</button>
              </div>
            </div>
            <div className={styles.item}>
              <p className={styles.question}>{t('kboix.register', 'question.train.respiratory')}</p>
              <div className={styles.answers}>
                <button className={styles.option} data-selected={values.train_respiratory === 'yes'} onClick={() => updateData('train_respiratory', 'yes')}>{t('kboix.register', 'yes')}</button>
                <button className={styles.option} data-selected={values.train_respiratory === 'no'} onClick={() => updateData('train_respiratory', 'no')}>{t('kboix.register', 'no')}</button>
              </div>
            </div>
            <div className={styles.item}>
              <p className={styles.question}>{t('kboix.register', 'question.heat')}</p>
              <div className={styles.answers}>
                <button className={styles.option} data-selected={values.heat === 'yes'} onClick={() => updateData('heat', 'yes')}>{t('kboix.register', 'yes')}</button>
                <button className={styles.option} data-selected={values.heat === 'no'} onClick={() => updateData('heat', 'no')}>{t('kboix.register', 'no')}</button>
              </div>
            </div>
            <div className={styles.item}>
              <p className={styles.question}>{t('kboix.register', 'question.train.high.carb')}</p>
              <div className={styles.answers}>
                <button className={styles.option} data-selected={values.train_high_carb === 'yes'} onClick={() => updateData('train_high_carb', 'yes')}>{t('kboix.register', 'yes')}</button>
                <button className={styles.option} data-selected={values.train_high_carb === 'no'} onClick={() => updateData('train_high_carb', 'no')}>{t('kboix.register', 'no')}</button>
              </div>
            </div>
            <div className={styles.item}>
              <p className={styles.question}>{t('kboix.register', 'question.tread')}</p>
              <div className={styles.answers}>
                <button className={styles.option} data-selected={values.tread === 1} onClick={() => updateData('tread', 1)}>{t('kboix', 'tread.type.1')}</button>
                <button className={styles.option} data-selected={values.tread === 2} onClick={() => updateData('tread', 2)}>{t('kboix', 'tread.type.2')}</button>
                <button className={styles.option} data-selected={values.tread === 3} onClick={() => updateData('tread', 3)}>{t('kboix', 'tread.type.3')}</button>
              </div>
            </div>
            <div className={styles.item}>
              <p className={styles.question}>{t('kboix.register', 'question.running.mode')}</p>
              <div className={styles.answers}>
                <button className={styles.option} data-selected={values.mode === 1} onClick={() => updateData('mode', 1)}>{t('kboix', 'running.mode.1')}</button>
                <button className={styles.option} data-selected={values.mode === 2} onClick={() => updateData('mode', 2)}>{t('kboix', 'running.mode.2')}</button>
                <button className={styles.option} data-selected={values.mode === 3} onClick={() => updateData('mode', 3)}>{t('kboix', 'running.mode.3')}</button>
              </div>
            </div>
            <div className={styles.item}>
              <p className={styles.question}>{t('kboix.register', 'question.foot.width')}</p>
              <div className={styles.answers}>
                <button className={styles.option} data-selected={values.foot_width === 1} onClick={() => updateData('foot_width', 1)}>{t('kboix', 'foot.width.type.1')}</button>
                <button className={styles.option} data-selected={values.foot_width === 2} onClick={() => updateData('foot_width', 2)}>{t('kboix', 'foot.width.type.2')}</button>
              </div>
            </div>
            <div className={styles.item}>
              <div className={styles.finish}>
                <Timer speed={0.2} finish={state === 'success'} restart={![ 'success', 'saving' ].includes(state)}></Timer>
                {state === 'error' ? <p className={styles.finishText}>{t('kboix.register', 'saving.error')}</p> : <p className={styles.finishText}>{t('kboix.register', 'saving.desc')}</p>}
                <button data-hidden={state !== 'error'} disabled={state !== 'error'} className={styles.nextButton} onClick={saveData}>{t('kboix', 'retry')}</button>
              </div>
            </div>
          </motion.div>
        </div>
        <div data-hidden={step === steps.length - 1} className={styles.stepper}>
          <div>
            <span>{t('kboix', 'step')} {step + 1}/{steps.length}</span>
          </div>
        </div>
      </div>
    </MotionConfig>
  );
}
